import React, {useState, useEffect, useMemo} from 'react';
import {createApiClient} from '../../services/ApiClient';
import {useParams} from "react-router";
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    Tab,
    Tabs,
    Button,
    IconButton,
    ButtonGroup,
    Avatar,
    Stack,
    Divider,
    alpha,
    useTheme,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    School as SchoolIcon,
    QuestionAnswer as QuestionIcon,
    Timer as TimerIcon,
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    TrendingDown as TrendingDownIcon,
    Group as GroupIcon,
    Quiz as QuizIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import {Link, useLocation} from 'react-router';

const Analytics = ({accessToken, claims, courseSpecific = false}) => {
    const {courseId} = useParams();
    const location = useLocation();
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const urlParams = new URLSearchParams(location.search);
    const tabFromUrl = urlParams.get('tab');
    const initialTab = tabFromUrl ? parseInt(tabFromUrl) : 0;
    const [selectedTab, setSelectedTab] = useState(initialTab);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [dateRange, setDateRange] = useState(7);
    const [refreshing, setRefreshing] = useState(false);
    const [successCompletionData, setSuccessCompletionData] = useState(null);
    const [questionPerformanceData, setQuestionPerformanceData] = useState(null);
    const [questionOverviewData, setQuestionOverviewData] = useState(null);
    const [learningPatternData, setLearningPatternData] = useState(null);
    const [studentData, setStudentData] = useState(null);

    // API Client erstellen
    const apiClient = useMemo(() => {
        return accessToken ? createApiClient(accessToken) : null;
    }, [accessToken]);

    const getSuccessRateColor = (rate) => {
        if (rate >= 80) return theme.palette.success.main;
        if (rate >= 60) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    const getSuccessRateIcon = (rate) => {
        if (rate >= 80) return <TrendingUpIcon sx={{color: 'success.main'}}/>;
        if (rate >= 60) return <TrendingUpIcon sx={{color: 'warning.main'}}/>;
        return <TrendingDownIcon sx={{color: 'error.main'}}/>;
    };

    useEffect(() => {
        if (apiClient) {
            fetchAnalytics();
            if (selectedTab === 2 && courseSpecific) {
                fetchStudentData();
            }
        }
    }, [apiClient, courseId, dateRange, selectedTab]);

    const fetchStudentData = async () => {
        if (courseSpecific && courseId && apiClient) {
            try {
                const data = await apiClient.get(`/api/analytics/course/${courseId}/students`);
                setStudentData(data);
            } catch (error) {
                console.error('Failed to fetch student data:', error);
            }
        }
    };

    const fetchAnalytics = async () => {
        try {
            setRefreshing(true);
            if (!apiClient) {
                console.error('No API client available');
                // Setze leere Daten statt Mock
                setAnalyticsData({
                    overview: {
                        totalStudents: 0,
                        activeStudents: 0,
                        avgSuccessRate: 0,
                        totalQuestions: 0,
                        answeredQuestions: 0,
                    },
                    courseStats: [],
                    topicStats: []
                });
                return;
            }

            // Verschiedene Endpoints je nach Ansicht
            const endpoint = courseSpecific && courseId
                ? `/api/analytics/course/${courseId}?days=${dateRange}`
                : `/api/analytics/overview?days=${dateRange}`;

            const data = await apiClient.get(endpoint);
            setAnalyticsData(data);

            if (courseSpecific && courseId) {
                try {
                    const successData = await apiClient.get(`/api/analytics/success-completion/${courseId}?days=${dateRange}`);
                    setSuccessCompletionData(successData);
                } catch (error) {
                    console.error('Failed to fetch success/completion data:', error);
                }
                try {
                    const performanceData = await apiClient.get(`/api/analytics/question-performance/${courseId}`);
                    setQuestionPerformanceData(performanceData);
                } catch (error) {
                    console.error('Failed to fetch question performance data:', error);
                }
            } else {
                try {
                    const overviewData = await apiClient.get(`/api/analytics/question-performance-overview?days=${dateRange}`);
                    setQuestionOverviewData(overviewData);

                } catch (error) {
                    console.error('Failed to fetch question overview data:', error);
                }

            }
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            setAnalyticsData({
                overview: {
                    totalStudents: 0,
                    activeStudents: 0,
                    avgSuccessRate: 0,
                    totalQuestions: 0,
                    answeredQuestions: 0,
                },
                courseStats: [],
                topicStats: []
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <LinearProgress sx={{width: '50%'}}/>
            </Box>
        );
    }

    if (!analyticsData) {
        return (
            <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
                <Typography variant="h6" color="error">
                    Failed to load analytics data.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{mt: 4, mb: 4, pb:10}}>
            {/* Header mit Back-Button für kurs-spezifische Ansicht */}
            <Box sx={{mb: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Box>
                        {courseSpecific && (
                            <Button
                                component={Link}
                                to="/teacher/dashboard"
                                startIcon={<ArrowBackIcon/>}
                                sx={{mb: 2}}
                            >
                                Back to Dashboard
                            </Button>
                        )}

                        <Typography variant="h4" gutterBottom>
                            {courseSpecific ? 'Course Analytics' : 'Analytics Dashboard'}
                        </Typography>

                        {courseSpecific && analyticsData.courseName && (
                            <Typography variant="h6" color="textSecondary">
                                {analyticsData.courseName}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                        <ButtonGroup size="small">
                            <Button variant={dateRange === 7 ? "contained" : "outlined"}
                                    onClick={() => setDateRange(7)}>
                                7 Days
                            </Button>
                            <Button variant={dateRange === 30 ? "contained" : "outlined"}
                                    onClick={() => setDateRange(30)}>
                                30 Days
                            </Button>
                            <Button variant={dateRange === 90 ? "contained" : "outlined"}
                                    onClick={() => setDateRange(90)}>
                                90 Days
                            </Button>
                            <Button variant={dateRange === 99999 ? "contained" : "outlined"}
                                    onClick={() => setDateRange(99999)}>
                                All Time
                            </Button>
                        </ButtonGroup>
                        <IconButton onClick={fetchAnalytics} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={20}/> : <RefreshIcon/>}
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            {/* Overview Cards - 7 kompakte KPIs - DYNAMISCH basierend auf courseSpecific */}
            <Grid container spacing={2} sx={{mb: 4}}>
                <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent sx={{p: 2}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box flex={1}>
                                    <Typography variant="caption" color="textSecondary" gutterBottom>
                                        Active Students
                                    </Typography>
                                    <Typography variant="h6">
                                        {refreshing ? (
                                            <CircularProgress size={16}/>
                                        ) : (
                                            `${analyticsData.overview.activeStudents}/${analyticsData.overview.totalStudents}`
                                        )}
                                    </Typography>
                                </Box>
                                <SchoolIcon color="primary" sx={{fontSize: 28, ml: 1}}/>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent sx={{p: 2}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box flex={1}>
                                    <Typography variant="caption" color="textSecondary" gutterBottom>
                                        Avg Success Rate
                                    </Typography>
                                    <Typography variant="h6">
                                        {analyticsData.overview.avgSuccessRate}%
                                    </Typography>
                                </Box>
                                <TrendingUpIcon color="success" sx={{fontSize: 28, ml: 1}}/>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent sx={{p: 2}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box flex={1}>
                                    <Typography variant="caption" color="textSecondary" gutterBottom>
                                        Total Questions
                                    </Typography>
                                    <Typography variant="h6">
                                        {analyticsData.overview.totalQuestions}
                                    </Typography>
                                </Box>
                                <QuestionIcon color="info" sx={{fontSize: 28, ml: 1}}/>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent sx={{p: 2}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box flex={1}>
                                    <Typography variant="caption" color="textSecondary" gutterBottom>
                                        Answered
                                    </Typography>
                                    <Typography variant="h6">
                                        {analyticsData.overview.answeredQuestions}
                                    </Typography>
                                </Box>
                                <TimerIcon color="warning" sx={{fontSize: 28, ml: 1}}/>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* DYNAMISCHE KPIs - je nach Ansicht */}
                {courseSpecific ? (
                    // KURS-SPEZIFISCHE KPIs (Topics)
                    <>
                        <Grid size={{xs: 6, sm: 4, md: 2, xl: "grow"}}>
                            <Card sx={{height: '100%', bgcolor: alpha(theme.palette.success.main, 0.08)}}>
                                <CardContent sx={{p: 2}}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" gutterBottom>
                                            High Performing
                                        </Typography>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box flex={1}>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {analyticsData.topicStats?.filter(t => t.successRate >= 80).length || 0}
                                                </Typography>
                                                <Typography variant="caption" color="success.main">
                                                    Topics
                                                </Typography>
                                            </Box>
                                            <CheckCircleIcon color="success" sx={{fontSize: 28, ml: 1}}/>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                            <Card sx={{height: '100%', bgcolor: alpha(theme.palette.error.main, 0.08)}}>
                                <CardContent sx={{p: 2}}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" gutterBottom>
                                            Need Attention
                                        </Typography>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box flex={1}>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {analyticsData.topicStats?.filter(t => t.successRate < 60).length || 0}
                                                </Typography>
                                                <Typography variant="caption" color="error.main">
                                                    Topics
                                                </Typography>
                                            </Box>
                                            <WarningIcon color="error" sx={{fontSize: 28, ml: 1}}/>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                ) : (
                    // ALLGEMEINE KPIs (Courses)
                    <>
                        <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                            <Card sx={{height: '100%'}}>
                                <CardContent sx={{p: 2}}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box flex={1}>
                                            <Typography variant="caption" color="textSecondary" gutterBottom>
                                                Total Courses
                                            </Typography>
                                            <Typography variant="h6">
                                                {analyticsData.courseStats?.length || 0}
                                            </Typography>
                                        </Box>
                                        <SchoolIcon color="secondary" sx={{fontSize: 28, ml: 1}}/>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                            <Card sx={{height: '100%', bgcolor: alpha(theme.palette.success.main, 0.08)}}>
                                <CardContent sx={{p: 2}}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" gutterBottom>
                                            Best Performing
                                        </Typography>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box flex={1}>
                                                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                                    {analyticsData.courseStats?.length > 0
                                                        ? [...analyticsData.courseStats].sort((a, b) => b.successRate - a.successRate)[0].name
                                                        : 'N/A'}
                                                </Typography>
                                                {analyticsData.courseStats?.length > 0 && (
                                                    <Typography variant="caption" color="success.main"
                                                                fontWeight="bold">
                                                        {[...analyticsData.courseStats].sort((a, b) => b.successRate - a.successRate)[0].successRate}%
                                                    </Typography>
                                                )}
                                            </Box>
                                            <CheckCircleIcon color="success" sx={{fontSize: 28, ml: 1}}/>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{xs: 6, sm: 4, md: 2, lg: "grow"}}>
                            <Card sx={{height: '100%', bgcolor: alpha(theme.palette.error.main, 0.08)}}>
                                <CardContent sx={{p: 2}}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" gutterBottom>
                                            Needs Attention
                                        </Typography>
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Box flex={1}>
                                                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                                    {analyticsData.courseStats?.length > 0
                                                        ? [...analyticsData.courseStats].sort((a, b) => a.successRate - b.successRate)[0].name
                                                        : 'N/A'}
                                                </Typography>
                                                {analyticsData.courseStats?.length > 0 && (
                                                    <Typography variant="caption" color="error.main" fontWeight="bold">
                                                        {[...analyticsData.courseStats].sort((a, b) => a.successRate - b.successRate)[0].successRate}%
                                                    </Typography>
                                                )}
                                            </Box>
                                            <WarningIcon color="error" sx={{fontSize: 28, ml: 1}}/>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
            </Grid>
            {/* Tabs für verschiedene Ansichten */}
            <Paper sx={{width: '100%', mb: 3}}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
                    <Tab label={courseSpecific ? "Topic Overview" : "Course Overview"}/>
                    <Tab label="Question Performance"/>
                    <Tab label="Detailed Insights"/>
                </Tabs>
            </Paper>

            {selectedTab === 0 && (
                <Grid container spacing={3} sx={{width: '100%', flex: 1}}>
                    {/* Bei kurs-spezifischer Ansicht zeigen wir Topics statt Courses */}
                    {courseSpecific ? (
                        // NUR Topic Cards - KEINE Summary, KEINE Success/Completion Analysis
                        analyticsData.topicStats?.map((topic) => (
                            <Grid size={{xs: 12, md: 6, lg: 4}} key={topic.name}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        transition: 'all 0.3s',
                                        border: '2px solid',
                                        borderColor: 'transparent',
                                        position: 'relative',
                                        '&:hover': {
                                            boxShadow: 4,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    {/* Performance Badge */}
                                    {topic.successRate >= 80 && (
                                        <Chip
                                            icon={<CheckCircleIcon/>}
                                            label="Mastered"
                                            size="small"
                                            color="success"
                                        />
                                    )}
                                    {topic.successRate < 60 && (
                                        <Chip
                                            icon={<WarningIcon/>}
                                            label="Needs Review"
                                            size="small"
                                            color="error"

                                        />
                                    )}
                                    <CardContent sx={{p: 2}}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 2
                                        }}>
                                            <Box flex={1} sx={{mr: 1}}>
                                                <Typography variant="h6" gutterBottom>
                                                    {topic.name}
                                                </Typography>
                                                <Chip
                                                    icon={<QuizIcon/>}
                                                    label={`${topic.questions} Questions`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            <Avatar
                                                sx={{
                                                    bgcolor: getSuccessRateColor(topic.successRate),
                                                    width: 48,
                                                    height: 48,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Typography variant="body1" color="white" fontWeight="bold">
                                                    {topic.successRate}%
                                                </Typography>
                                            </Avatar>
                                        </Box>

                                        <Divider sx={{my: 1.5}}/>

                                        {/* Success Rate Progress */}
                                        <Box sx={{mb: 2}}>
                                            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Success Rate
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color={getSuccessRateColor(topic.successRate)}
                                                >
                                                    {topic.successRate}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={topic.successRate}
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    bgcolor: alpha(getSuccessRateColor(topic.successRate), 0.1),
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 3,
                                                        bgcolor: getSuccessRateColor(topic.successRate),
                                                    },
                                                }}
                                            />
                                        </Box>

                                        {/* Topic Metrics - FIXED: Grid statt Flex für bessere Kontrolle */}
                                        {(topic.totalAttempts !== undefined || topic.avgTime || topic.completionRate !== undefined) && (
                                            <Grid container spacing={1}>
                                                {topic.totalAttempts !== undefined && (
                                                    <Grid size={4}>
                                                        <Box sx={{
                                                            textAlign: 'center',
                                                            p: 0.5,
                                                            bgcolor: 'background.default',
                                                            borderRadius: 1,
                                                            height: '100%'
                                                        }}>
                                                            <Typography
                                                                variant="subtitle1"
                                                                color="primary"
                                                                fontWeight="bold"
                                                                sx={{lineHeight: 1.2}}
                                                            >
                                                                {topic.totalAttempts}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                display="block"
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    lineHeight: 1.2,
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                Attempts
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                                {topic.avgTime && (
                                                    <Grid size={4}>
                                                        <Box sx={{
                                                            textAlign: 'center',
                                                            p: 0.5,
                                                            bgcolor: 'background.default',
                                                            borderRadius: 1,
                                                            height: '100%'
                                                        }}>
                                                            <Typography
                                                                variant="subtitle1"
                                                                color="secondary"
                                                                fontWeight="bold"
                                                                sx={{lineHeight: 1.2}}
                                                            >
                                                                {topic.avgTime}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                display="block"
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    lineHeight: 1.2,
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                Avg Time
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                                {topic.completionRate !== undefined && (
                                                    <Grid size={4}>
                                                        <Box sx={{
                                                            textAlign: 'center',
                                                            p: 0.5,
                                                            bgcolor: 'background.default',
                                                            borderRadius: 1,
                                                            height: '100%'
                                                        }}>
                                                            <Typography
                                                                variant="subtitle1"
                                                                color="info.main"
                                                                fontWeight="bold"
                                                                sx={{lineHeight: 1.2}}
                                                            >
                                                                {topic.completionRate}%
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                display="block"
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    lineHeight: 1.2,
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                Complete
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    ) : (
                        // COURSE OVERVIEW - dieser Teil bleibt EXAKT wie er ist
                        analyticsData.courseStats?.map((course) => (
                            <Grid size={{xs: 12, md: 6, lg: 4}} key={course.name}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        transition: 'all 0.3s',
                                        cursor: 'pointer',
                                        border: '2px solid',
                                        borderColor: 'transparent',
                                        position: 'relative',
                                        overflow: 'visible',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 6,
                                            borderColor: 'primary.main',
                                        },
                                    }}
                                    onClick={() => {
                                        window.location.href = `/teacher/analytics/course/${course.id || course.name}`;
                                    }}
                                >
                                    {/* Performance Badge */}
                                    {course.successRate >= 80 && (
                                        <Chip
                                            icon={<CheckCircleIcon/>}
                                            label="High Performance"
                                            size="small"
                                            color="success"
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: 16,
                                                zIndex: 1,
                                            }}
                                        />
                                    )}
                                    {course.successRate < 60 && (
                                        <Chip
                                            icon={<WarningIcon/>}
                                            label="Needs Attention"
                                            size="small"
                                            color="error"
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: 16,
                                                zIndex: 1,
                                            }}
                                        />
                                    )}

                                    <CardContent>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 3
                                        }}>
                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    {course.name}
                                                </Typography>
                                                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                                    <Chip
                                                        icon={<GroupIcon/>}
                                                        label={`${course.students} Students`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Box>
                                            <Avatar
                                                sx={{
                                                    bgcolor: getSuccessRateColor(course.successRate),
                                                    width: 56,
                                                    height: 56,
                                                }}
                                            >
                                                <SchoolIcon/>
                                            </Avatar>
                                        </Box>

                                        <Divider sx={{my: 2}}/>

                                        {/* Success Rate Progress */}
                                        <Box sx={{mb: 3}}>
                                            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Success Rate
                                                </Typography>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                    {getSuccessRateIcon(course.successRate)}
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color={getSuccessRateColor(course.successRate)}
                                                    >
                                                        {course.successRate}%
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={course.successRate}
                                                sx={{
                                                    height: 10,
                                                    borderRadius: 5,
                                                    bgcolor: alpha(getSuccessRateColor(course.successRate), 0.1),
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 5,
                                                        bgcolor: getSuccessRateColor(course.successRate),
                                                    },
                                                }}
                                            />
                                        </Box>

                                        {/* Quick Stats */}
                                        <Stack direction="row" spacing={2}>
                                            <Box sx={{
                                                flex: 1,
                                                textAlign: 'center',
                                                p: 1,
                                                bgcolor: 'background.default',
                                                borderRadius: 1
                                            }}>
                                                <Typography variant="h6" color="primary">
                                                    {course.activeStudents || 0}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Active
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            )}

            {selectedTab === 1 && (
                <Grid container spacing={3} sx={{width: '100%', flex: 1}}>
                    {courseSpecific ? (
                        questionPerformanceData ? (
                            <>
                                <Grid size={12}>
                                    <Paper sx={{p: 3}}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 3
                                        }}>
                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    Questions Students Struggle With Most
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Based
                                                    on {questionPerformanceData.overallStats.totalUniqueStudents} students
                                                    • {questionPerformanceData.overallStats.timeframe}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={`Avg Success Rate: ${questionPerformanceData.overallStats.averageSuccessRate}%`}
                                                sx={{
                                                    bgcolor: questionPerformanceData.overallStats.averageSuccessRate < 60 ? '#ffebee' : '#e8f5e9',
                                                    color: questionPerformanceData.overallStats.averageSuccessRate < 60 ? '#c62828' : '#2e7d32',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </Box>

                                        {questionPerformanceData.strugglingQuestions.length === 0 ? (
                                            <Typography color="textSecondary" align="center" sx={{py: 4}}>
                                                Not enough data yet. Questions need at least 5 attempts to appear here.
                                            </Typography>
                                        ) : (
                                            questionPerformanceData.strugglingQuestions.map((question, index) => (
                                                <Card
                                                    key={question.questionId}
                                                    sx={{
                                                        mb: 2,
                                                        p: 2,
                                                        border: '1px solid',
                                                        borderColor: question.successRate < 40 ? '#ffcdd2' :
                                                            question.successRate > 75 ? '#c8e6c9' :
                                                                'divider',
                                                        bgcolor: question.successRate < 40 ? '#ffebee' :
                                                            question.successRate > 75 ? '#e8f5e9' :
                                                                'background.paper',
                                                        '&:hover': {
                                                            boxShadow: 3
                                                        }
                                                    }}
                                                >
                                                    <CardContent>
                                                        <Grid container alignItems="flex-start" spacing={2}>
                                                            <Grid size={12}>
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    gap: 1,
                                                                    mb: 1
                                                                }}>
                                                                    <Chip
                                                                        label={`#${index + 1}`}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: question.successRate > 75 ? 'success.main' : 'primary.main',
                                                                            color: 'white',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    />
                                                                    <Box sx={{flex: 1}}>
                                                                        <Typography variant="subtitle1"
                                                                                    fontWeight="bold"
                                                                                    color="text.primary">
                                                                            {question.questionText}
                                                                        </Typography>
                                                                        <Typography variant="body2"
                                                                                    color="textSecondary"
                                                                                    sx={{mt: 0.5}}>
                                                                            {question.topic} → {question.section}
                                                                        </Typography>
                                                                        <Box sx={{display: 'flex', gap: 1, mt: 1}}>
                                                                            <Chip
                                                                                label={question.questionType}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                sx={{borderColor: 'grey.400'}}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                </Box>

                                                                {/* Common Wrong Answers for Multiple Choice */}
                                                                {question.commonWrongAnswers.length > 0 && (
                                                                    <Box sx={{
                                                                        mt: 2,
                                                                        p: 2,
                                                                        bgcolor: 'grey.100',
                                                                        borderRadius: 1,
                                                                        border: '1px solid',
                                                                        borderColor: 'grey.300'
                                                                    }}>
                                                                        <Typography variant="body2" fontWeight="bold"
                                                                                    gutterBottom>
                                                                            Common Wrong Answers:
                                                                        </Typography>
                                                                        {question.commonWrongAnswers.map((wa, i) => (
                                                                            <Typography key={i} variant="body2"
                                                                                        color="error.dark" sx={{ml: 1}}>
                                                                                • {JSON.stringify(wa.answer)} ({wa.count} times)
                                                                            </Typography>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                                {question.text_answers && question.text_answers.length > 0 && (
                                                                    <Box sx={{
                                                                        mt: 2,
                                                                        p: 2,
                                                                        bgcolor: 'rgba(227, 242, 253, 0.5)', // Helles Blau
                                                                        borderRadius: 1,
                                                                        border: '1px solid',
                                                                        borderColor: 'primary.light'
                                                                    }}>
                                                                        <Typography variant="body2" fontWeight="bold"
                                                                                    gutterBottom color="primary.dark">
                                                                            Beispielantworten (neueste zuerst):
                                                                        </Typography>
                                                                        {question.text_answers.map((answer, i) => (
                                                                            <Box key={i} sx={{
                                                                                py: 1,
                                                                                borderTop: i > 0 ? '1px dashed' : 'none',
                                                                                borderColor: 'grey.300'
                                                                            }}>
                                                                                <Typography variant="body2"
                                                                                            color="text.secondary"
                                                                                            sx={{fontStyle: 'italic'}}>
                                                                                    "{answer}"
                                                                                </Typography>
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                                <Divider sx={{my: 2}}/>
                                                                <Grid container spacing={2}>
                                                                    <Grid size={6}>
                                                                        <Box sx={{
                                                                            textAlign: 'center',
                                                                            p: 2,
                                                                            bgcolor: 'background.paper',
                                                                            borderRadius: 1,
                                                                            border: '1px solid',
                                                                            borderColor: 'grey.300'
                                                                        }}>
                                                                            <Typography variant="body2"
                                                                                        color="textSecondary">
                                                                                Success Rate
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="h4"
                                                                                sx={{
                                                                                    color: question.successRate < 50 ? '#d32f2f' : '#388e3c',
                                                                                    fontWeight: 'bold'
                                                                                }}
                                                                            >
                                                                                {question.successRate}%
                                                                            </Typography>
                                                                            <Typography variant="caption"
                                                                                        color="textSecondary">
                                                                                {question.correctAnswers} / {question.totalAttempts}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid size={6}>
                                                                        <Box sx={{
                                                                            textAlign: 'center',
                                                                            p: 2,
                                                                            bgcolor: 'background.paper',
                                                                            borderRadius: 1,
                                                                            border: '1px solid',
                                                                            borderColor: 'grey.300'
                                                                        }}>
                                                                            <Typography variant="body2"
                                                                                        color="textSecondary">
                                                                                Avg Points
                                                                            </Typography>
                                                                            <Typography variant="h4"
                                                                                        color="primary.main"
                                                                                        fontWeight="bold">
                                                                                {question.avgPointsEarned}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>

                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={question.successRate}
                                                                    sx={{
                                                                        height: 10,
                                                                        borderRadius: 5,
                                                                        mt: 2,
                                                                        bgcolor: '#e0e0e0',
                                                                        '& .MuiLinearProgress-bar': {
                                                                            bgcolor: question.successRate < 50 ? '#ef5350' : '#66bb6a',
                                                                            borderRadius: 5
                                                                        }
                                                                    }}
                                                                />

                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    mt: 1
                                                                }}>
                                                                    <Typography variant="caption"
                                                                                sx={{color: '#d32f2f'}}>
                                                                        {question.incorrectAnswers} incorrect
                                                                    </Typography>
                                                                    <Typography variant="caption"
                                                                                sx={{color: '#388e3c'}}>
                                                                        {question.correctAnswers} correct
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            ))
                                        )}

                                        {questionPerformanceData.strugglingQuestions.length > 0 && (
                                            <Box sx={{
                                                mt: 3,
                                                p: 2,
                                                bgcolor: '#e3f2fd',
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: '#90caf9'
                                            }}>
                                                <Typography variant="body2" color="text.primary">
                                                    💡 <strong>Tip:</strong> Consider reviewing these topics in class or
                                                    providing additional resources for questions with success rates
                                                    below 50%.
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </>
                        ) : refreshing ? (
                            <Grid size={12}>
                                <Box display="flex" justifyContent="center" p={4}>
                                    <CircularProgress/>
                                </Box>
                            </Grid>
                        ) : (
                            <Grid size={12}>
                                <Paper sx={{p: 3}}>
                                    <Typography variant="h6" color="textSecondary" align="center">
                                        No question performance data available yet.
                                    </Typography>
                                </Paper>
                            </Grid>
                        )
                    ) : (
                        // ALLGEMEINE ÜBERSICHT (NEU)
                        questionOverviewData ? (
                            <Grid size={12}>
                                <Paper sx={{p: 3}}>
                                    <Typography variant="h6" gutterBottom>
                                        Question Performance by Course
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                                        Click on a course to see detailed question analysis
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {questionOverviewData.coursePerformanceStats?.map((course) => (
                                            <Grid size={{xs: 12, md: 6, lg: 4}} key={course.id}>
                                                <Card
                                                    sx={{
                                                        p: 2,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            boxShadow: 3,
                                                            transform: 'translateY(-2px)'
                                                        }
                                                    }}
                                                    onClick={() => window.location.href = `/teacher/analytics/course/${course.id}?tab=1`}
                                                >
                                                    <Typography variant="h6" gutterBottom>{course.name}</Typography>
                                                    <Box sx={{mt: 2}}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            mb: 1
                                                        }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Avg Question Success
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color={course.avgQuestionSuccess < 50 ? 'error' : 'success.main'}
                                                                fontWeight="bold"
                                                            >
                                                                {course.avgQuestionSuccess}%
                                                            </Typography>
                                                        </Box>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={course.avgQuestionSuccess}
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 4,
                                                                bgcolor: 'grey.300',
                                                                '& .MuiLinearProgress-bar': {
                                                                    borderRadius: 4,
                                                                    bgcolor: course.avgQuestionSuccess < 50 ? 'error.main' : 'success.main'
                                                                }
                                                            }}
                                                        />
                                                        <Box sx={{mt: 2, display: 'flex', gap: 1}}>
                                                            <Chip
                                                                size="small"
                                                                label={`${course.strugglingQuestions} difficult`}
                                                                color={course.strugglingQuestions > 0 ? "error" : "default"}
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                size="small"
                                                                label={`${course.totalQuestions} total`}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {/* Top 5 schwierigste Fragen - Gruppiert nach Kurs */}
                                    {questionOverviewData.topDifficultQuestions?.length > 0 && (
                                        <Box sx={{mt: 4}}>
                                            <Typography variant="h6" gutterBottom>
                                                Most Challenging Questions by Course
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                                                Top 5 questions per course with lowest success rates
                                            </Typography>

                                            {/* Gruppiere Fragen nach Kurs */}
                                            {Object.entries(
                                                questionOverviewData.topDifficultQuestions.reduce((acc, question) => {
                                                    if (!acc[question.courseName]) {
                                                        acc[question.courseName] = [];
                                                    }
                                                    acc[question.courseName].push(question);
                                                    return acc;
                                                }, {})
                                            ).map(([courseName, questions]) => (
                                                <Box key={courseName} sx={{mb: 4}}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight="bold"
                                                        sx={{
                                                            mb: 2,
                                                            pb: 1,
                                                            borderBottom: '2px solid',
                                                            borderColor: 'primary.main'
                                                        }}
                                                    >
                                                        {courseName}
                                                    </Typography>

                                                    {questions.slice(0, 5).map((question, index) => {
                                                        let statusColor = 'error';
                                                        if (question.successRate >= 80) {
                                                            statusColor = 'success'; // Grün für hohe Raten
                                                        } else if (question.successRate >= 50) {
                                                            statusColor = 'warning'; // Orange für mittlere Raten
                                                        }
                                                        return (
                                                            <Card key={question.questionId} sx={{
                                                                mb: 2,
                                                                p: 2,
                                                                ml: 2,
                                                                borderLeft: 4,
                                                                borderColor: `${statusColor}.main`
                                                            }}>
                                                                <Grid container alignItems="center" spacing={2}>
                                                                    <Grid size={{xs: 12, md: 8}}>
                                                                        <Box sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                            mb: 1
                                                                        }}>
                                                                            <Chip
                                                                                label={`#${index + 1}`}
                                                                                size="small"
                                                                                color={statusColor}
                                                                                sx={{fontWeight: 'bold'}}
                                                                            />
                                                                            <Typography variant="subtitle2"
                                                                                        fontWeight="bold">
                                                                                {question.topicName}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="textSecondary"
                                                                            sx={{
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                display: '-webkit-box',
                                                                                WebkitLineClamp: 3,
                                                                                WebkitBoxOrient: 'vertical',
                                                                            }}
                                                                        >
                                                                            {question.questionText}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid size={{xs: 12, md: 4}}>
                                                                        <Box sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'flex-end',
                                                                            gap: 2
                                                                        }}>
                                                                            <Box sx={{textAlign: 'center'}}>
                                                                                <Typography variant="h6"
                                                                                            color={`${statusColor}.dark`}>
                                                                                    {question.successRate}%
                                                                                </Typography>
                                                                                <Typography variant="caption"
                                                                                            color="textSecondary">
                                                                                    Success Rate
                                                                                </Typography>
                                                                            </Box>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => window.location.href = `/teacher/analytics/course/${question.courseId}?tab=1`}
                                                                            >
                                                                                View Course
                                                                            </Button>
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>
                                                            </Card>
                                                        );
                                                    })}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>


                        ) : refreshing ? (
                            <Grid size={12}>
                                <Box display="flex" justifyContent="center" p={4}>
                                    <CircularProgress/>
                                </Box>
                            </Grid>
                        ) : (
                            <Grid size={12}>
                                <Paper sx={{p: 3}}>
                                    <Typography variant="h6" color="textSecondary" align="center">
                                        No question performance data available yet.
                                    </Typography>
                                </Paper>
                            </Grid>
                        )
                    )}
                </Grid>
            )}

            {selectedTab === 2 && (
                <Grid container spacing={3}>
                    <Grid size={12}>
                        {courseSpecific ? (
                            // KURS-SPEZIFISCHE ANSICHT
                            <Box>
                                {/* Topic Drill-Down */}
                                <Paper sx={{p: 3, mb: 3}}>
                                    <Typography variant="h6" gutterBottom>
                                        Topic Performance Matrix
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                                        Detailed breakdown by criticality level
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {/* Critical Topics */}
                                        <Grid size={{xs: 12, md: 4}}>
                                            <Card sx={{
                                                border: 2,
                                                borderColor: 'error.main',
                                                height: '100%'
                                            }}>
                                                <CardContent>
                                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                                        <WarningIcon color="error" sx={{mr: 1}}/>
                                                        <Typography variant="h6" color="error">
                                                            Critical Topics
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Success rate below 40%
                                                    </Typography>
                                                    <Divider sx={{my: 2}}/>

                                                    {analyticsData.topicStats?.filter(t => t.successRate < 40).map(topic => (
                                                        <Box key={topic.name} sx={{mb: 2}}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}>
                                                                <Typography
                                                                    variant="subtitle2">{topic.name}</Typography>
                                                                <Chip
                                                                    label={`${topic.successRate}%`}
                                                                    size="small"
                                                                    color="error"
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {topic.questions} questions
                                                                • {topic.totalAttempts || 0} attempts
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={topic.successRate}
                                                                color="error"
                                                                sx={{mt: 1, height: 4}}
                                                            />
                                                        </Box>
                                                    ))}

                                                    {analyticsData.topicStats?.filter(t => t.successRate < 40).length === 0 && (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    align="center">
                                                            No critical topics
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Warning Topics */}
                                        <Grid size={{xs: 12, md: 4}}>
                                            <Card sx={{
                                                border: 2,
                                                borderColor: 'warning.main',
                                                height: '100%'
                                            }}>
                                                <CardContent>
                                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                                        <InfoIcon color="warning" sx={{mr: 1}}/>
                                                        <Typography variant="h6" color="warning.main">
                                                            Needs Attention
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Success rate 40-79%
                                                    </Typography>
                                                    <Divider sx={{my: 2}}/>

                                                    {analyticsData.topicStats?.filter(t => t.successRate >= 40 && t.successRate < 80).map(topic => (
                                                        <Box key={topic.name} sx={{mb: 2}}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}>
                                                                <Typography
                                                                    variant="subtitle2">{topic.name}</Typography>
                                                                <Chip
                                                                    label={`${topic.successRate}%`}
                                                                    size="small"
                                                                    color="warning"
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {topic.questions} questions
                                                                • {topic.totalAttempts || 0} attempts
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={topic.successRate}
                                                                color="warning"
                                                                sx={{mt: 1, height: 4}}
                                                            />
                                                        </Box>
                                                    ))}

                                                    {analyticsData.topicStats?.filter(t => t.successRate >= 40 && t.successRate < 80).length === 0 && (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    align="center">
                                                            No topics in this range
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Good Topics */}
                                        <Grid size={{xs: 12, md: 4}}>
                                            <Card sx={{
                                                border: 2,
                                                borderColor: 'success.main',
                                                height: '100%'
                                            }}>
                                                <CardContent>
                                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                                        <CheckCircleIcon color="success" sx={{mr: 1}}/>
                                                        <Typography variant="h6" color="success.main">
                                                            Well Performing
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Success rate 80% and above
                                                    </Typography>
                                                    <Divider sx={{my: 2}}/>

                                                    {analyticsData.topicStats?.filter(t => t.successRate >= 80).map(topic => (
                                                        <Box key={topic.name} sx={{mb: 2}}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}>
                                                                <Typography
                                                                    variant="subtitle2">{topic.name}</Typography>
                                                                <Chip
                                                                    label={`${topic.successRate}%`}
                                                                    size="small"
                                                                    color="success"
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {topic.questions} questions
                                                                • {topic.totalAttempts || 0} attempts
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={topic.successRate}
                                                                color="success"
                                                                sx={{mt: 1, height: 4}}
                                                            />
                                                        </Box>
                                                    ))}

                                                    {analyticsData.topicStats?.filter(t => t.successRate >= 80).length === 0 && (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    align="center">
                                                            No topics above 80%
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Student Performance Table */}
                                <Paper sx={{p: 3}}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 3
                                    }}>
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                Anonymous Student Performance
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Individual student progress (anonymized)
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<RefreshIcon/>}
                                            onClick={() => fetchStudentData && fetchStudentData()}
                                        >
                                            Refresh
                                        </Button>
                                    </Box>

                                    {studentData ? (
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Student ID</TableCell>
                                                        <TableCell align="center">Topics Attempted</TableCell>
                                                        <TableCell align="center">Questions Answered</TableCell>
                                                        <TableCell align="center">Success Rate</TableCell>
                                                        <TableCell align="center">Completion Rate</TableCell>
                                                        <TableCell align="center">Last Active</TableCell>
                                                        <TableCell align="center">Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {studentData.students?.map((student) => (
                                                        <TableRow key={student.anonymousId}>
                                                            <TableCell>
                                                                <Typography variant="body2" fontFamily="monospace">
                                                                    {student.anonymousId}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {student.topicsAttempted}/{student.totalTopics}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {student.questionsAnswered}/{student.totalQuestions}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: 1
                                                                }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color={
                                                                            student.successRate >= 80 ? 'success.main' :
                                                                                student.successRate >= 60 ? 'warning.main' :
                                                                                    'error.main'
                                                                        }
                                                                        fontWeight="bold"
                                                                    >
                                                                        {student.successRate}%
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={student.completionRate}
                                                                    sx={{
                                                                        height: 6,
                                                                        borderRadius: 3,
                                                                        width: '80px',
                                                                        margin: '0 auto'
                                                                    }}
                                                                />
                                                                <Typography variant="caption">
                                                                    {student.completionRate}%
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Typography variant="caption">
                                                                    {/* Prüfe, ob ein Datum vorhanden ist, bevor du es formatierst */}
                                                                    {student.lastActive ? new Date(student.lastActive).toLocaleDateString() : 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={
                                                                        student.completionRate === 100 ? 'Completed' :
                                                                            student.isActive ? 'Active' : 'Inactive'
                                                                    }
                                                                    size="small"
                                                                    color={
                                                                        student.completionRate === 100 ? 'success' :
                                                                            student.isActive ? 'primary' : 'default'
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                                            <CircularProgress/>
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        ) : (
                            <Box>
                                {/* Course Performance Distribution */}
                                <Paper sx={{p: 3, mb: 3}}>
                                    <Typography variant="h6" gutterBottom>
                                        Course Performance Distribution
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                                        Overview of all courses by performance category
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {/* Critical Courses */}
                                        <Grid size={{xs: 12, md: 4}}>
                                            <Card sx={{
                                                border: 2,
                                                borderColor: 'error.main',
                                                height: '100%'
                                            }}>
                                                <CardContent>
                                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                                        <WarningIcon color="error" sx={{mr: 1}}/>
                                                        <Typography variant="h6" color="error">
                                                            Critical Courses
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Success rate below 40%
                                                    </Typography>
                                                    <Divider sx={{my: 2}}/>

                                                    {analyticsData.courseStats?.filter(c => c.successRate < 40).map(course => (
                                                        <Box
                                                            key={course.name}
                                                            sx={{
                                                                mb: 2,
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    bgcolor: 'action.hover',
                                                                    borderRadius: 1,
                                                                    p: 1
                                                                }
                                                            }}
                                                            onClick={() => window.location.href = `/teacher/analytics/course/${course.id}?tab=2`}
                                                        >
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}>
                                                                <Typography
                                                                    variant="subtitle2">{course.name}</Typography>
                                                                <Chip
                                                                    label={`${course.successRate}%`}
                                                                    size="small"
                                                                    color="error"
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {course.students} students
                                                                • {course.activeStudents || 0} active
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={course.successRate}
                                                                color="error"
                                                                sx={{mt: 1, height: 4}}
                                                            />
                                                        </Box>
                                                    ))}

                                                    {analyticsData.courseStats?.filter(c => c.successRate < 40).length === 0 && (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    align="center">
                                                            No critical courses
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Needs Attention Courses */}
                                        <Grid size={{xs: 12, md: 4}}>
                                            <Card sx={{
                                                border: 2,
                                                borderColor: 'warning.main',
                                                height: '100%'
                                            }}>
                                                <CardContent>
                                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                                        <InfoIcon color="warning" sx={{mr: 1}}/>
                                                        <Typography variant="h6" color="warning.main">
                                                            Needs Attention
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Success rate 40-79%
                                                    </Typography>
                                                    <Divider sx={{my: 2}}/>

                                                    {analyticsData.courseStats?.filter(c => c.successRate >= 40 && c.successRate < 80).map(course => (
                                                        <Box
                                                            key={course.name}
                                                            sx={{
                                                                mb: 2,
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    bgcolor: 'action.hover',
                                                                    borderRadius: 1,
                                                                    p: 1
                                                                }
                                                            }}
                                                            onClick={() => window.location.href = `/teacher/analytics/course/${course.id}?tab=2`}
                                                        >
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}>
                                                                <Typography
                                                                    variant="subtitle2">{course.name}</Typography>
                                                                <Chip
                                                                    label={`${course.successRate}%`}
                                                                    size="small"
                                                                    color="warning"
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {course.students} students
                                                                • {course.activeStudents || 0} active
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={course.successRate}
                                                                color="warning"
                                                                sx={{mt: 1, height: 4}}
                                                            />
                                                        </Box>
                                                    ))}

                                                    {analyticsData.courseStats?.filter(c => c.successRate >= 40 && c.successRate < 80).length === 0 && (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    align="center">
                                                            No courses in this range
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Well Performing Courses */}
                                        <Grid size={{xs: 12, md: 4}}>
                                            <Card sx={{
                                                border: 2,
                                                borderColor: 'success.main',
                                                height: '100%'
                                            }}>
                                                <CardContent>
                                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                                        <CheckCircleIcon color="success" sx={{mr: 1}}/>
                                                        <Typography variant="h6" color="success.main">
                                                            Well Performing
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Success rate 80% and above
                                                    </Typography>
                                                    <Divider sx={{my: 2}}/>

                                                    {analyticsData.courseStats?.filter(c => c.successRate >= 80).map(course => (
                                                        <Box
                                                            key={course.name}
                                                            sx={{
                                                                mb: 2,
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    bgcolor: 'action.hover',
                                                                    borderRadius: 1,
                                                                    p: 1
                                                                }
                                                            }}
                                                            onClick={() => window.location.href = `/teacher/analytics/course/${course.id}?tab=2`}
                                                        >
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}>
                                                                <Typography
                                                                    variant="subtitle2">{course.name}</Typography>
                                                                <Chip
                                                                    label={`${course.successRate}%`}
                                                                    size="small"
                                                                    color="success"
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {course.students} students
                                                                • {course.activeStudents || 0} active
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={course.successRate}
                                                                color="success"
                                                                sx={{mt: 1, height: 4}}
                                                            />
                                                        </Box>
                                                    ))}

                                                    {analyticsData.courseStats?.filter(c => c.successRate >= 80).length === 0 && (
                                                        <Typography variant="body2" color="textSecondary"
                                                                    align="center">
                                                            No courses above 80%
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/*// ALLGEMEINE ÜBERSICHT (nicht kurs-spezifisch)*/}
                                {/* Course Comparison Table */}
                                <Paper sx={{p: 3}}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 3
                                    }}>
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                Course Comparison Overview
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Detailed metrics for all your courses
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {analyticsData.courseStats && analyticsData.courseStats.length > 0 ? (
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Course Name</TableCell>
                                                        <TableCell align="center">Total Students</TableCell>
                                                        <TableCell align="center">Active Students</TableCell>
                                                        <TableCell align="center">Success Rate</TableCell>
                                                        <TableCell align="center">Total Questions</TableCell>
                                                        <TableCell align="center">Avg Completion</TableCell>
                                                        <TableCell align="center">Status</TableCell>
                                                        <TableCell align="center">Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {[...analyticsData.courseStats]
                                                        .sort((a, b) => a.successRate - b.successRate) // Sortiere nach Success Rate (niedrigste zuerst)
                                                        .map((course) => (
                                                            <TableRow key={course.id} hover>
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {course.name}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {course.students}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: 1
                                                                    }}>
                                                                        <Typography variant="body2">
                                                                            {course.activeStudents || 0}
                                                                        </Typography>
                                                                        <Typography variant="caption"
                                                                                    color="textSecondary">
                                                                            ({Math.round((course.activeStudents || 0) / course.students * 100)}%)
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: 1
                                                                    }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color={
                                                                                course.successRate >= 80 ? 'success.main' :
                                                                                    course.successRate >= 60 ? 'warning.main' :
                                                                                        'error.main'
                                                                            }
                                                                            fontWeight="bold"
                                                                        >
                                                                            {course.successRate}%
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {course.totalQuestions || 'N/A'}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={course.completionRate || 0}
                                                                        sx={{
                                                                            height: 6,
                                                                            borderRadius: 3,
                                                                            width: '80px',
                                                                            margin: '0 auto'
                                                                        }}
                                                                    />
                                                                    <Typography variant="caption">
                                                                        {course.completionRate || 0}%
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Chip
                                                                        label={
                                                                            course.successRate >= 80 ? 'Excellent' :
                                                                                course.successRate >= 60 ? 'Good' :
                                                                                    course.successRate >= 40 ? 'Fair' :
                                                                                        'Needs Help'
                                                                        }
                                                                        size="small"
                                                                        color={
                                                                            course.successRate >= 80 ? 'success' :
                                                                                course.successRate >= 60 ? 'primary' :
                                                                                    course.successRate >= 40 ? 'warning' :
                                                                                        'error'
                                                                        }
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={() => window.location.href = `/teacher/analytics/course/${course.id}?tab=2`}
                                                                    >
                                                                        View Details
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                                            <Typography variant="body1" color="textSecondary">
                                                No course data available
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>

                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}
        </Container>
    );
};

export default Analytics;